import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  Box,
  Button,
  Grid,
  Stack,
  Tab,
  Typography,
  styled,
} from "@mui/material";
import contactAPI, { Contact } from "apis/ContactAPI";
import { SchedulePaymentChargeResponse } from "apis/ScheduleAPI";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import FinanceiroTab from "./FinanceiroTab";
import { format } from "date-fns";
import React, { SyntheticEvent, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { SCHEDULE_STATUS } from "../event-utils";
import {
  AppointmentDetailsContainer,
  SpanDateValue,
  SpanTitle,
  SpanValue,
  StyledLink,
  StyledTypography,
} from "./AppointmentDetails.styles";

interface RentalData {
  sportCourtId: string;
  sportCourtValue: number;
  sportCourtRentalTime: number;
  rentalQuantity: number;
}

interface History {
  date: number;
  message: string;
}

interface Appointment {
  id: string;
  schedulePlanId?: string | null;
  contactId: string;
  contactName: string;
  observation: string;
  whenDate: number | Date;
  timeFromTo: string;
  responsibleId: string;
  rentalData: RentalData;
  type: "RENT" | "OTHER_TYPE";
  status: string;
  history?: History[];
}

interface AppointmentDetailsProps {
  appointment: Appointment | null;
  onEdit: () => void;
  stripeEnabled?: boolean;
  paymentCharge?: SchedulePaymentChargeResponse | null;
  onChargeCreated?: () => Promise<void>;
}

const TABS = {
  DETAILS: "1",
  HISTORY: "2",
  FINANCEIRO: "3",
};

const HistoryItem: React.FC<{ history: History }> = ({ history }) => {
  const getDate = (timestamp: number) => {
    return format(new Date(timestamp), "dd/MM/yyyy HH:mm:ss");
  };

  return (
    <StyledHistoryListItem>
      <Typography variant="body2">
        <SpanTitle>{getDate(history.date)}:</SpanTitle>
        <SpanValue>{history.message}</SpanValue>
      </Typography>
    </StyledHistoryListItem>
  );
};

const AppointmentDetails: React.FC<AppointmentDetailsProps> = ({
  appointment,
  onEdit,
  stripeEnabled,
  paymentCharge,
  onChargeCreated,
}) => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(TABS.DETAILS);

  if (!appointment) return null;

  const {
    whenDate,
    timeFromTo,
    contactName,
    contactId,
    rentalData,
    observation,
    history,
    status,
  } = appointment;

  const valorTotal = rentalData.sportCourtValue;

  function formatDate(date: Date, appointmentTimeFromTo: string): string {
    const diasDaSemana = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
    const meses = [
      "jan",
      "fev",
      "mar",
      "abr",
      "mai",
      "jun",
      "jul",
      "ago",
      "set",
      "out",
      "nov",
      "dez",
    ];

    const diaSemana = diasDaSemana[date.getDay()];
    const dia = date.getDate();
    const mes = meses[date.getMonth()];
    const ano = date.getFullYear();

    return `${diaSemana}, ${dia} de ${mes} ${ano} | às ${appointmentTimeFromTo.replace(
      " - ",
      " às "
    )}`;
  }

  function getStatusLabel(statusValue: string): string {
    return (
      SCHEDULE_STATUS.find(
        (scheduleStatus) => scheduleStatus.value === statusValue
      )?.name || ""
    );
  }

  function contactClick(contactIdToOpen: string) {
    navigate(`/main/contact/view/${contactIdToOpen}`);
  }

  function whatsapp() {
    if (!contactId) return;

    contactAPI.getById(contactId).then((response) => {
      const result = response.data as Contact;

      if (!result.phones[0]) {
        toast.error("Não existe nenhum telefone cadastrado para este contato");
        return;
      }

      const rawPhone = result.phones[0].telephone;
      const formattedPhone = `55${rawPhone.replace(/\D/g, "")}`;
      window.open(
        `https://api.whatsapp.com/send?phone=${formattedPhone}`,
        "_blank"
      );
    });
  }

  const showFinanceiro = stripeEnabled && !!appointment?.id;

  const handleChange = (_: SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  return (
    <>
      <TabContext value={tabValue}>
        <StyledTabList onChange={handleChange}>
          <StyledTab label="Detalhes" value={TABS.DETAILS} />
          <StyledTab label="Histórico de alterações" value={TABS.HISTORY} />
          {showFinanceiro && (
            <StyledTab label="Financeiro" value={TABS.FINANCEIRO} />
          )}
        </StyledTabList>

        <StyledTabPanel value={TABS.DETAILS}>
          <AppointmentDetailsContainer>
            <Stack spacing={1.25}>
              <Typography variant="body2">
                <SpanTitle>Data:</SpanTitle>
                <SpanDateValue>
                  {formatDate(new Date(whenDate), timeFromTo)}
                </SpanDateValue>
              </Typography>
              <Typography variant="body2">
                <SpanTitle>Status:</SpanTitle>
                <SpanValue>{getStatusLabel(status)}</SpanValue>
              </Typography>
              <Typography variant="body2">
                <SpanTitle>Contato:</SpanTitle>
                <SpanValue>
                  <StyledLink
                    component="button"
                    variant="body2"
                    onClick={() => contactClick(contactId)}
                  >
                    {contactName || ""}
                  </StyledLink>
                </SpanValue>
              </Typography>
              <Typography variant="body2">
                <SpanTitle>Valor:</SpanTitle>
                <SpanValue>{`R$ ${valorTotal
                  .toFixed(2)
                  .replace(".", ",")}`}</SpanValue>
              </Typography>
              <Typography variant="body2">
                <SpanTitle>Observação:</SpanTitle>
                <SpanValue>{observation || "Não informado"}</SpanValue>
              </Typography>
            </Stack>

            <StyledTypography variant="body2" onClick={whatsapp}>
              <WhatsAppIcon sx={{ color: "#4caf50", marginRight: "4px" }} />
              <SpanTitle>Entrar em contato</SpanTitle>
            </StyledTypography>
          </AppointmentDetailsContainer>
        </StyledTabPanel>

        <StyledTabPanel value={TABS.HISTORY}>
          <AppointmentDetailsContainer>
            <StyledHistoryList>
              {history && history.length > 0 ? (
                history.map((historyItem, index) => (
                  <HistoryItem key={index} history={historyItem} />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma alteração registrada.
                </Typography>
              )}
            </StyledHistoryList>
          </AppointmentDetailsContainer>
        </StyledTabPanel>

        {showFinanceiro && (
          <StyledTabPanel value={TABS.FINANCEIRO}>
            <AppointmentDetailsContainer>
              <FinanceiroTab
                scheduleId={appointment!.id}
                paymentCharge={paymentCharge}
                onChargeCreated={onChargeCreated ?? (() => Promise.resolve())}
              />
            </AppointmentDetailsContainer>
          </StyledTabPanel>
        )}
      </TabContext>

      <Grid item xs={12}>
        <Box mt={1}>
          <Button variant="contained" onClick={onEdit}>
            Editar
          </Button>
        </Box>
      </Grid>
    </>
  );
};

const StyledHistoryList = styled("ul")(() => ({
  padding: "0 10px",
  margin: 0,
}));

const StyledHistoryListItem = styled("li")(() => ({
  marginBottom: "8px",
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 700,
  color: theme.palette.text.primary,
  textTransform: "none",
  minHeight: 42,
}));

const StyledTabList = styled(TabList)(({ theme }) => ({
  marginBottom: 16,
  "& .MuiTabs-indicator": {
    height: 3,
    borderRadius: 3,
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiTabs-flexContainer": {
      justifyContent: "flex-start",
      gap: 4,
    },
  },
}));

const StyledTabPanel = styled(TabPanel)(() => ({
  padding: 0,
}));

export default AppointmentDetails;
